import React, { useState, useEffect, useRef, useContext } from 'react';
import { translate, getClientDomain, getToken, getPackageTypeFilterOptions } from '../../../../utils/Common';
import AuthContext from '../../../../contexts/auth';
import { usePlatforms } from '../../../../hooks/usePlatforms';
import { useProviders } from '../../../../hooks/useProviders';
import * as FaIcons from 'react-icons/fa';
import styles from './IntegratedFilter.module.css';

const IntegratedFilter = ({ onFilterChange, activeFilters = {} }) => {
  const userContext = useContext(AuthContext);
  const { platforms, getPlatforms } = usePlatforms();
  const { getProviders } = useProviders();
  
  const [selectedFilterType, setSelectedFilterType] = useState(null); // 'plataforma', 'compañia', 'tipoPaquete'
  const [platformsList, setPlatformsList] = useState([]);
  const [companiesList, setCompaniesList] = useState([]);
  // Starts true: between the first render and the effect firing, a false
  // flag with an empty list paints the "nothing here" state for a frame,
  // which reads as the screen finding nothing and then changing its mind.
  const [isLoadingPlatforms, setIsLoadingPlatforms] = useState(true);
  // Starts true: between the first render and the effect firing, a false
  // flag with an empty list paints the "nothing here" state for a frame,
  // which reads as the screen finding nothing and then changing its mind.
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const wrapperRef = useRef(null);

  // Tipos de filtro disponibles
  const filterTypes = [
    {
      id: 'plataforma',
      label: translate('dashboard.platforms.name') || 'Plataforma',
      icon: <FaIcons.FaWarehouse size={16} />
    },
    {
      id: 'compañia',
      label: translate('global.business') || 'Compañía',
      icon: <FaIcons.FaBuilding size={16} />
    },
    {
      id: 'tipoPaquete',
      label: translate('global.typeOfPackage') || 'Tipo de Paquete',
      icon: <FaIcons.FaBox size={16} />
    }
  ];

  // Tipos de paquete según país: USA (0,1) | Panama/BR (0,1,2 y 3)
  const packageTypeOptions = getPackageTypeFilterOptions();
  const packageTypeIcons = {
    returned: <FaIcons.FaUndo size={14} />,
    dropoff: <FaIcons.FaBoxOpen size={14} />,
    regular: <FaIcons.FaBox size={14} />
  };
  const packageTypes = packageTypeOptions.map(opt => ({
    ...opt,
    label: translate(opt.label),
    icon: packageTypeIcons[opt.iconKey] || packageTypeIcons.regular
  }));

  // Cargar plataformas
  useEffect(() => {
    if (selectedFilterType === 'plataforma' && platformsList.length === 0) {
      const loadPlatforms = async () => {
        setIsLoadingPlatforms(true);
        try {
          const abortController = new AbortController();
          // Si ya hay plataformas en el hook, usarlas; si no, cargarlas
          if (platforms && platforms.length > 0) {
            setPlatformsList(platforms);
          } else {
            const loadedPlatforms = await getPlatforms(userContext.user?.IdCliente, abortController.signal);
            setPlatformsList(loadedPlatforms || []);
          }
        } catch (error) {
          console.error('Error loading platforms:', error);
          setPlatformsList([]);
        } finally {
          setIsLoadingPlatforms(false);
        }
      };
      loadPlatforms();
    }
  }, [selectedFilterType, getPlatforms, userContext.user?.IdCliente, platforms]);

  // Cargar compañías (disponible para todos los roles)
  useEffect(() => {
    if (selectedFilterType === 'compañia' && companiesList.length === 0) {
      const loadCompanies = async () => {
        setIsLoadingCompanies(true);
        try {
          const abortController = new AbortController();
          const providers = await getProviders('all', abortController.signal);
          if (providers && providers.length > 0) {
            setCompaniesList(providers);
          } else {
            setCompaniesList([]);
          }
        } catch (error) {
          console.error('Error loading companies:', error);
          setCompaniesList([]);
        } finally {
          setIsLoadingCompanies(false);
        }
      };
      loadCompanies();
    }
  }, [selectedFilterType, companiesList.length, getProviders]);

  // Cerrar al hacer clic fuera
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setSelectedFilterType(null);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const handleFilterTypeSelect = (typeId) => {
    if (selectedFilterType === typeId) {
      setSelectedFilterType(null);
      setSearchTerm('');
    } else {
      setSelectedFilterType(typeId);
      setSearchTerm('');
    }
  };

  const handleOptionSelect = (filterType, option) => {
    const newFilters = { ...activeFilters };
    
    if (filterType === 'plataforma') {
      // Usar DescPlataforma (External ID) o boxitLocationId como fallback
      newFilters.plataforma = option.DescPlataforma || option.boxitLocationId?.toString() || option.locationName;
    } else if (filterType === 'compañia') {
      // El campo usuario se convertirá a IdUsuario en la función findPackages
      newFilters.usuario = option.IdUsuario?.toString();
    } else if (filterType === 'tipoPaquete') {
      newFilters.typePkg = option.value;
    }

    onFilterChange(newFilters);
    setSelectedFilterType(null);
    setSearchTerm('');
  };

  const handleClearFilter = (filterType) => {
    const newFilters = { ...activeFilters };
    if (filterType === 'plataforma') {
      delete newFilters.plataforma;
    } else if (filterType === 'compañia') {
      delete newFilters.usuario;
    } else if (filterType === 'tipoPaquete') {
      delete newFilters.typePkg;
    }
    onFilterChange(newFilters);
  };

  const getSelectedOptionLabel = (filterType) => {
    if (filterType === 'plataforma' && activeFilters.plataforma) {
      const platform = platformsList.find(p => 
        (p.DescPlataforma?.toUpperCase() === activeFilters.plataforma.toUpperCase()) || 
        (p.boxitLocationId?.toString() === activeFilters.plataforma) ||
        (p.locationName?.toUpperCase() === activeFilters.plataforma.toUpperCase())
      );
      return platform?.DescPlataforma || platform?.locationName || activeFilters.plataforma;
    }
    if (filterType === 'compañia' && activeFilters.usuario) {
      const company = companiesList.find(c => c.IdUsuario?.toString() === activeFilters.usuario);
      return company ? `${company.IdUsuario} - ${company.NombreEmpresa}` : activeFilters.usuario;
    }
    if (filterType === 'tipoPaquete' && activeFilters.typePkg) {
      const type = packageTypes.find(t => t.value === activeFilters.typePkg);
      return type?.label || activeFilters.typePkg;
    }
    return null;
  };

  const getFilteredOptions = () => {
    if (!selectedFilterType) return [];

    if (selectedFilterType === 'plataforma') {
      return platformsList.filter(p => {
        const name = (p.DescPlataforma || p.locationName || '').toLowerCase();
        return name.includes(searchTerm.toLowerCase());
      });
    }
    
    if (selectedFilterType === 'compañia') {
      return companiesList.filter(c => {
        const search = `${c.IdUsuario} - ${c.NombreEmpresa}`.toLowerCase();
        return search.includes(searchTerm.toLowerCase());
      });
    }
    
    if (selectedFilterType === 'tipoPaquete') {
      return packageTypes.filter(t => 
        t.label.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return [];
  };

  const filteredOptions = getFilteredOptions();
  const hasActiveFilter = selectedFilterType && 
    ((selectedFilterType === 'plataforma' && activeFilters.plataforma) ||
     (selectedFilterType === 'compañia' && activeFilters.usuario) ||
     (selectedFilterType === 'tipoPaquete' && activeFilters.typePkg));

  return (
    <div ref={wrapperRef} className={styles.integratedFilter}>
      {/* Selector principal de tipo de filtro */}
      <div className={styles.filterTypeSelector}>
        {filterTypes.map((type) => {
          const isSelected = selectedFilterType === type.id;
          const hasValue = getSelectedOptionLabel(type.id);
          
          return (
            <div key={type.id} className={styles.filterTypeWrapper}>
              <button
                type="button"
                className={`${styles.filterTypeButton} ${isSelected ? styles.active : ''} ${hasValue ? styles.hasValue : ''}`}
                onClick={() => handleFilterTypeSelect(type.id)}
              >
                <span className={styles.filterTypeIcon}>{type.icon}</span>
                <span className={styles.filterTypeLabel}>{type.label}</span>
                {hasValue && (
                  <span className={styles.filterTypeValue}>
                    {getSelectedOptionLabel(type.id)}
                  </span>
                )}
                <FaIcons.FaChevronDown 
                  className={`${styles.chevron} ${isSelected ? styles.chevronUp : ''}`} 
                  size={12} 
                />
              </button>
              
              {/* Botón de limpiar si hay valor seleccionado */}
              {hasValue && (
                <button
                  type="button"
                  className={styles.clearButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClearFilter(type.id);
                  }}
                  title={translate('global.clean') || 'Limpiar'}
                >
                  <FaIcons.FaTimes size={12} />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Panel deslizante con opciones */}
      {selectedFilterType && (
        <div className={styles.optionsPanel}>
          {/* Barra de búsqueda dentro del panel */}
          <div className={styles.searchBar}>
            <FaIcons.FaSearch className={styles.searchIcon} size={14} />
            <input
              type="text"
              className={styles.searchInput}
              placeholder={translate('global.search') + '...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
            {searchTerm && (
              <button
                type="button"
                className={styles.clearSearchButton}
                onClick={() => setSearchTerm('')}
              >
                <FaIcons.FaTimes size={12} />
              </button>
            )}
          </div>

          {/* Lista de opciones */}
          <div className={styles.optionsList}>
            {selectedFilterType === 'plataforma' && isLoadingPlatforms && (
              <div className={styles.loading}>
                <div className="inline-block size-4 animate-spin rounded-full border-2 border-current border-r-transparent spinner-border-sm text-primary" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            )}
            
            {selectedFilterType === 'compañia' && isLoadingCompanies && (
              <div className={styles.loading}>
                <div className="inline-block size-4 animate-spin rounded-full border-2 border-current border-r-transparent spinner-border-sm text-primary" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            )}

            {!isLoadingPlatforms && !isLoadingCompanies && filteredOptions.length === 0 && (
              <div className={styles.noResults}>
                {translate('global.noResults') || 'No se encontraron resultados'}
              </div>
            )}

            {filteredOptions.map((option, index) => {
              let label = '';
              let isActive = false;

              if (selectedFilterType === 'plataforma') {
                label = option.DescPlataforma || option.locationName || '';
                const optionValue = option.DescPlataforma || option.boxitLocationId?.toString() || option.locationName;
                isActive = activeFilters.plataforma && 
                  (activeFilters.plataforma.toUpperCase() === optionValue?.toUpperCase() ||
                   activeFilters.plataforma === optionValue);
              } else if (selectedFilterType === 'compañia') {
                label = `${option.IdUsuario} - ${option.NombreEmpresa}`;
                isActive = activeFilters.usuario === option.IdUsuario?.toString();
              } else if (selectedFilterType === 'tipoPaquete') {
                label = option.label;
                isActive = activeFilters.typePkg === option.value;
              }

              return (
                <button
                  key={index}
                  type="button"
                  className={`${styles.optionButton} ${isActive ? styles.optionActive : ''}`}
                  onClick={() => handleOptionSelect(selectedFilterType, option)}
                >
                  <span className={styles.optionLabelWrap}>
                    {selectedFilterType === 'tipoPaquete' && option.icon && <span className={styles.optionIcon}>{option.icon}</span>}
                    <span>{label}</span>
                  </span>
                  {isActive && <FaIcons.FaCheck className={styles.checkIcon} size={12} />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default IntegratedFilter;
